import { useEffect, useState } from "react";

/** COMPONENT */
import { Box, CircularProgress, Typography } from "@mui/material";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import { notiError } from "../../../component/notification/notifications";
import DeductTable from "../../depositRefund/components/DeductTable";

/** API */
import StallReclaimApi, {
  StallReclaimDetail,
} from "../../../api/stall-reclaim/stall-reclaim.api";

/** UTILS */
import { colors } from "../../../constants/colors";
import { stallReclaimStatusColor, stallReclaimStatusLabel } from "../../../constants/stallReclaim";
import { dateTimeToView } from "../../../utils/date.utils";
import {
  DASH,
  bahtToView,
  dateOnlyToView,
  getDepositRefundError as getStallReclaimError,
  toAmount,
} from "../../depositRefund/depositRefund.utils";

/** STYLE */
import * as Style from "../stallReclaim.style";

type Props = {
  id: number;
  onClose: () => void;
};

const textOrDash = (value?: string | null) => value || DASH;

type RowRenderer = {
  label: string;
  value: (detail: StallReclaimDetail) => React.ReactNode;
  /** true = ไม่แสดงแถวเลยถ้าไม่มีค่า (แถวว่างเปล่าไม่ได้ให้ข้อมูลอะไร) */
  hideWhenEmpty?: boolean;
  isEmpty?: (detail: StallReclaimDetail) => boolean;
};

/**
 * ประกาศแถวเป็นข้อมูล ไม่ใช่เงื่อนไขกระจายใน JSX — ลำดับใน array คือลำดับบนจอ
 * แถวที่มีเฉพาะตอนพิจารณาแล้วใช้ hideWhenEmpty เพื่อไม่ให้ขึ้นแถวขีดเปล่าๆ ตอนยังรอพิจารณา
 */
const ROWS: RowRenderer[] = [
  { label: "เลขที่คำสั่ง", value: (d) => textOrDash(d.deposit_refund_no) },
  { label: "เลขที่สัญญา", value: (d) => textOrDash(d.lease_agreement_no) },
  { label: "ชื่อผู้เช่าแผงค้า", value: (d) => textOrDash(d.tenant_name) },
  { label: "โซน", value: (d) => textOrDash(d.zone_name) },
  { label: "แผงค้า", value: (d) => textOrDash(d.stall_name) },
  { label: "เลขแผงค้า", value: (d) => textOrDash(d.stall_number) },
  { label: "ประเภทแผงค้า", value: (d) => textOrDash(d.stall_type_name) },
  { label: "รายละเอียด", value: (d) => textOrDash(d.request_note) },
  {
    label: "วันเวลาที่สร้าง",
    // dateTimeToView(undefined) format วันนี้เงียบๆ ต้อง guard ก่อนเสมอ
    value: (d) => (d.request_date ? dateTimeToView(d.request_date) : DASH),
  },
  {
    label: "กำหนดคืนพื้นที่",
    // DATE ที่ serialize เป็น UTC ISO ต้อง format ด้วย timezone Asia/Bangkok
    // ผิด 1 วันในเรื่องกำหนดคืนพื้นที่มีผลทางกฎหมาย
    value: (d) => dateOnlyToView(d.due_date),
  },
  {
    label: "ผู้ออกคำสั่ง",
    value: (d) => textOrDash(d.request_by_name),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.request_by_name,
  },
  {
    label: "วันเวลาที่พิจารณา",
    value: (d) => (d.reviewed_date ? dateTimeToView(d.reviewed_date) : DASH),
  },
  { label: "ผู้พิจารณา", value: (d) => textOrDash(d.reviewed_name) },
  {
    label: "สถานะ",
    value: (d) => (
      <Typography component="span" sx={{ fontSize: 16, color: stallReclaimStatusColor(d.status) }}>
        {stallReclaimStatusLabel(d.status)}
      </Typography>
    ),
  },
  {
    label: "ยอดเงินที่ได้รับการพิจารณา",
    value: (d) => bahtToView(d.approved_amount),
    // ยังไม่พิจารณา หรือไม่อนุมัติ = ไม่มียอด ไม่ต้องโชว์แถว
    hideWhenEmpty: true,
    isEmpty: (d) => toAmount(d.approved_amount) === null,
  },
  {
    label: "วันที่คืนเงิน",
    value: (d) => dateOnlyToView(d.refund_date),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.refund_date,
  },
  {
    label: "เลขที่เอกสารอ้างอิง",
    value: (d) => textOrDash(d.doc_ref),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.doc_ref,
  },
  {
    label: "รายละเอียดการพิจารณา",
    value: (d) => textOrDash(d.review_note),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.review_note,
  },
  {
    label: "วันที่ยกเลิกสัญญาเช่า",
    value: (d) => dateOnlyToView(d.lease_canceled_date),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.lease_canceled_date,
  },
];

/** modal รายละเอียดคำสั่งเรียกคืนแผงค้า — อ่านอย่างเดียว ไม่มีปุ่มยืนยัน */
export default function ModalStallReclaimDetail({ id, onClose }: Props) {
  const [detail, setDetail] = useState<StallReclaimDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadDetail = async () => {
      setIsLoading(true);
      try {
        const res = await StallReclaimApi.findById(id);
        if (!isMounted) return;
        setDetail(res.data?.data || null);
      } catch (error) {
        if (!isMounted) return;
        setDetail(null);
        notiError(getStallReclaimError(error, "ดึงรายละเอียดคำสั่งไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadDetail();
    return () => {
      isMounted = false;
    };
  }, [id]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={32} sx={{ color: colors.themeMainColor }} />
        </Box>
      );
    }

    if (!detail) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
          <Typography sx={{ color: colors.textLightGray }}>
            ไม่พบคำสั่งเรียกคืนแผงค้าที่ต้องการ
          </Typography>
        </Box>
      );
    }

    const rows = ROWS.filter((row) => {
      if (!row.hideWhenEmpty) return true;
      return !row.isEmpty?.(detail);
    });

    return (
      <Box
        className="custom-scroll"
        sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}
      >
        <Box
          sx={{
            border: `1px solid ${colors.lightGray}`,
            borderRadius: "8px",
            backgroundColor: colors.white,
          }}
        >
          {rows.map((row) => (
            <Style.DetailRow key={row.label}>
              <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
                {row.label}
              </Typography>
              <Box sx={{ fontSize: 16, wordBreak: "break-word", whiteSpace: "pre-line" }}>
                {row.value(detail)}
              </Box>
            </Style.DetailRow>
          ))}
        </Box>

        {/* deducts เป็น [] เมื่อยังไม่พิจารณา หรือไม่อนุมัติ — DeductTable คืน null ให้เอง */}
        <Box sx={{ mt: 3 }}>
          <DeductTable deducts={detail.deducts} approvedAmount={detail.approved_amount} />
        </Box>
      </Box>
    );
  };

  return (
    <ModalCustom
      // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
      isShowProps
      // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
      autoResetModal={false}
      size="lg"
      closeButton
      title="รายละเอียดคำสั่งเรียกคืนแผงค้า"
      component={renderBody()}
      // ไม่ส่ง onSubmit/textBtnConfirm footer จึงไม่มีปุ่ม เหลือแค่ X มุมขวาบนตามดีไซน์
      onClose={onClose}
    />
  );
}
